import { parseOrThrow } from '_shared/_utilities/zod-schema-parser';
import { z } from 'zod';
import FeeScheduleCategorySchema from '_shared/fee-schedules/categories/fee-schedule-category-schema';
import RedlineChangeSchema, { RedlineChangeJSON } from "work/proposals/redlining/changes/_schemas/redline-change-schema";
import { RedlineChange } from 'work/proposals/redlining/changes/redline-change';

export type FeeScheduleCategoryRedlineJSON = z.infer<typeof FeeScheduleCategoryRedlineSchema.FeeScheduleCategoryRedlineJSONZ>;
export type FeeScheduleCategoryRedlineParams = z.infer<typeof FeeScheduleCategoryRedlineSchema.FeeScheduleCategoryRedlineZ>;

export default class FeeScheduleCategoryRedlineSchema {
  public static readonly FeeScheduleCategoryRedlineJSONZ = z.object({
    originalEntry: FeeScheduleCategorySchema.FeeScheduleCategoryJSONZ.nullish(),
    revisedEntry: FeeScheduleCategorySchema.FeeScheduleCategoryJSONZ.nullish(),
    currentEntry: FeeScheduleCategorySchema.FeeScheduleCategoryJSONZ.nullish(),
    sessionHistory: z.array(z.custom<RedlineChangeJSON>())
  });
  public static readonly FeeScheduleCategoryRedlineZ = z.object({
    originalEntry: FeeScheduleCategorySchema.FeeScheduleCategoryZ.nullable(),
    revisedEntry: FeeScheduleCategorySchema.FeeScheduleCategoryZ.nullable(),
    currentEntry: FeeScheduleCategorySchema.FeeScheduleCategoryZ.optional().nullable(),
    sessionHistory: z.array(z.instanceof(RedlineChange))
  });

  public static validate(values: unknown): FeeScheduleCategoryRedlineParams {
    return parseOrThrow(
      FeeScheduleCategoryRedlineSchema.FeeScheduleCategoryRedlineZ,
      values,
      { 
        logError: true,
        schemaName: "FeeScheduleCategoryRedlineSchema",
        operation: "validate",
        payloadSample: values 
      }
    );
  }

  public static validateJSON(values: unknown): FeeScheduleCategoryRedlineJSON {
    return parseOrThrow(
      FeeScheduleCategoryRedlineSchema.FeeScheduleCategoryRedlineJSONZ,
      values,
      { 
        logError: true,
        schemaName: "FeeScheduleCategoryRedlineSchema",
        operation: "validateJSON",
        payloadSample: values 
      }
    );
  }

  public static isValid(values: unknown): values is FeeScheduleCategoryRedlineParams {
    try {
      FeeScheduleCategoryRedlineSchema.validate(values);
      return true;
    } catch {
      return false;
    }
  }

  public static isValidJSON(values: unknown): values is FeeScheduleCategoryRedlineJSON {
    try {
      FeeScheduleCategoryRedlineSchema.validateJSON(values);
      return true;
    } catch {
      return false;
    }
  }

  public static deserialize(data: FeeScheduleCategoryRedlineJSON): FeeScheduleCategoryRedlineParams {
    const parsed = parseOrThrow(
      FeeScheduleCategoryRedlineSchema.FeeScheduleCategoryRedlineJSONZ,
      data,
      { 
        logError: true,
        schemaName: "FeeScheduleCategoryRedlineSchema",
        operation: "deserialize",
        payloadSample: data 
      }
    );
    return FeeScheduleCategoryRedlineSchema.codec.decode(parsed);
  }

  public static serialize(params: FeeScheduleCategoryRedlineParams): FeeScheduleCategoryRedlineJSON {
    const parsed = FeeScheduleCategoryRedlineSchema.validate(params);
    return FeeScheduleCategoryRedlineSchema.codec.encode(parsed);
  }

  private static readonly codec = z.codec(
    FeeScheduleCategoryRedlineSchema.FeeScheduleCategoryRedlineJSONZ,
    FeeScheduleCategoryRedlineSchema.FeeScheduleCategoryRedlineZ, {
    decode: (data) => ({
      originalEntry: data.originalEntry ? FeeScheduleCategorySchema.deserialize(data.originalEntry) : null,
      revisedEntry: data.revisedEntry ? FeeScheduleCategorySchema.deserialize(data.revisedEntry) : null,
      currentEntry: data.currentEntry ? FeeScheduleCategorySchema.deserialize(data.currentEntry) : undefined,
      sessionHistory: data.sessionHistory.map((changeData) => new RedlineChange(RedlineChangeSchema.deserialize(changeData)))
    }),
    encode: (value) => ({
      originalEntry: value.originalEntry ? FeeScheduleCategorySchema.serialize(value.originalEntry) : null,
      revisedEntry: value.revisedEntry ? FeeScheduleCategorySchema.serialize(value.revisedEntry) : null,
      currentEntry: value.currentEntry ? FeeScheduleCategorySchema.serialize(value.currentEntry) : undefined,
      sessionHistory: value.sessionHistory.map(RedlineChangeSchema.serialize)
    })
  });
}
